import PropTypes from 'prop-types';
import cloneDeep from 'lodash/cloneDeep';
import { UserConsumer } from '../../core/providers/UserProvider';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import SyncIcon from '@material-ui/icons/Sync';
import React, { Component } from 'react';
import GS from '../../grafsync/grafsync';
import ActionText from '../ActionText';
import Errored from '../Errored';
import Loading from '../Loading';
import { globalStyles as styles } from '../style/gstheme';
import { makeCancelable, sortObjects } from '../utils/utils';
import IntegrationCard from './IntegrationCard';
import IntegrationDetail from './IntegrationDetail';
import CreateIntegrationDialog from './CreateIntegrationDialog';

class IntegrationManager extends Component {

  constructor(props) {
    super(props);
    this.state = {
      selectedIntegration: null,
      isLoading: true,
      integrations: [],
      isErrored: null
    };
    this.cancellable = null;
    this.cancellable1 = null;
  }

  componentDidMount() {
    this.doGetIntegrations()
      .promise
      .then(integrations => {
        const sortedIntegrations = sortObjects(integrations, "label", "en");
        this.setState({ integrations: sortedIntegrations, isLoading: false });
      })
      .catch((e) => {
        if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
      });
  }

  doGetIntegrations() {
    this.cancellable = makeCancelable(GS.integrations());
    return this.cancellable;
  }

  doDeleteIntegration(id) {
    this.cancellable = makeCancelable(GS.deleteIntegration(id));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.cancellable1) this.cancellable1.cancel();
  }

  selectIntegration = (id) => this.setState({ selectedIntegration: id });

  doCreateIntegration(data) {
    this.cancellable1 = makeCancelable(GS.createIntegration(data));
    return this.cancellable1;
  }

  checkIntegration(integrationIRI, exists = true) {
    this.check = setTimeout(async () => {
      clearTimeout(this.check);
      try {
        const integrations = await this.doGetIntegrations().promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        if (integrations.some(integration => integration.id === integrationIRI && integration.publicKey) === exists) {
          const sortedIntegrations = sortObjects(integrations, "label", "en");
          this.setState({
            checking: false,
            integrations: sortedIntegrations,
            ...(exists) && { selectedIntegration: integrationIRI }
          });
        }
        else {
          if (this.cancellable && this.cancellable.isCancelled()) return;
          this.checkIntegration(integrationIRI, exists);
        }
      }
      catch (e) {
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ checking: false, isErrored: e });
      }
    }, 1000);
  }

  createIntegration = async (data) => {
    try {
      const response = await this.doCreateIntegration(data).promise;
      const responseData = await response.json();
      const integrationIRI = responseData.integration;
      this.setState({ checking: true }, () => {
        this.checkIntegration(integrationIRI);
      });
    }
    catch (e) {
      if (this.cancellable1 && !this.cancellable1.isCancelled()) this.setState({ isErrored: e });
    }
  };

  updateIntegration = (integrationIRI, endpoint, format, changesOnly) => {
    GS.updateIntegration(integrationIRI, {
      endpoint,
      format,
      changesOnly
    });
    let newIntegrations = cloneDeep(this.state.integrations);
    const updatedIntegration = newIntegrations.find(integration => integration.id === integrationIRI);
    updatedIntegration.integrationEndpoint = endpoint;
    updatedIntegration.format = format;
    this.setState({ integrations: newIntegrations });
  };

  deleteIntegration = (integrationIRI) => {
    try {
      this.setState({ selectedIntegration: null, checking: true }, async () => {
        await this.doDeleteIntegration(integrationIRI).promise;
        this.checkIntegration(integrationIRI, false);
      });
    }
    catch (e) {
      if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
    }
  };

  render() {

    const { classes } = this.props;
    const { isErrored, isLoading, integrations, selectedIntegration, checking } = this.state;

    if (isErrored) return <Errored data={isErrored} />;
    if (isLoading) return <Loading />;

    return (
      <UserConsumer>
        {({ subscriptions, invitations, activeSubscription }) => {
          const accessedSubscriptions = [...subscriptions, ...invitations];
          const subscription = accessedSubscriptions && accessedSubscriptions.find(resource => {
            if (resource.type === "Subscription" && resource.id === activeSubscription) return true;
            if (resource.type === "Invitation" && resource.subscription === activeSubscription) return true;
            return false;
          });
          const maxIntegrations = subscription.maxIntegrations;
          return <div>
            <div className={classes.controlPanel}>
              <Typography data-test="integration-count" variant="body2" style={{ marginTop: 6 }}><SyncIcon style={{ height: 20, width: 20, borderRadius: 4, backgroundColor: 'black', color: 'white', verticalAlign: 'middle', transform: 'translateY(-1px)' }} />&#160;&#160;{integrations.length}/{maxIntegrations}</Typography>
              <div data-test="integration-list-container" className={classes.tree} style={{ top: '120px' }}>
                {integrations.length === 0 ?
                  <ActionText offset="130px">No integrations.</ActionText>
                  :
                  <div data-test="integration-list">
                    {integrations.map((integration) => <IntegrationCard
                      key={integration.id}
                      id={integration.id}
                      name={integration.label["en"]}
                      onSelectIntegration={this.selectIntegration} />
                    )}
                  </div>
                }
              </div>
            </div>
            <div data-test="integration-detail" className={classes.dashboardPanel}>
              <div className={classes.dashboardAdd}>
                {integrations.length < maxIntegrations &&
                  <CreateIntegrationDialog onCreateIntegration={this.createIntegration} />
                }
              </div>
              {selectedIntegration ?
                <IntegrationDetail
                  key={selectedIntegration}
                  data={integrations.find(integration => integration.id === selectedIntegration)}
                  onUpdateIntegration={this.updateIntegration}
                  onDeleteIntegration={this.deleteIntegration} />
                :
                (integrations.length > 0 ?
                  <ActionText offset="180px">Select an integration to view and edit its detail.</ActionText>
                  :
                  <ActionText offset="180px">No integrations are available to edit.</ActionText>
                )
              }
            </div>
            {checking &&
              <div>
                <Dialog open={true} aria-labelledby="waiting-dialog-title" >
                  <DialogTitle
                    disableTypography
                    className={classes.mainHeading}
                    id="waiting-dialog-title">Processing Integration</DialogTitle>
                  <DialogContent>
                    <DialogContentText className={classes.dialogText}>Please wait. Your integration will be processed shortly.</DialogContentText>
                  </DialogContent>
                </Dialog>
              </div>
            }
          </div>;
        }}
      </UserConsumer>
    );
  }
}

IntegrationManager.propTypes = {
  classes: PropTypes.object
};

export default withStyles(styles)(IntegrationManager);